/**
 * Client-side idempotency keys for financial POST/PATCH operations.
 * Generate a fresh key per deliberate user action; reuse only when retrying the same request.
 */
export function createIdempotencyKey(prefix = "payroll") {
  const uuid =
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `${prefix}-${uuid}-${Date.now()}`;
}

export function idempotencyHeaders(prefix = "payroll", key) {
  return {
    "Idempotency-Key": key ?? createIdempotencyKey(prefix),
  };
}

export function getApiErrorDetail(error) {
  const detail = error?.response?.data?.detail ?? error?.response?.data;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => (typeof item === "string" ? item : item?.msg || JSON.stringify(item)))
      .join("; ");
  }
  if (detail && typeof detail === "object") {
    const parts = [];
    if (detail.message) parts.push(String(detail.message));
    else if (detail.code) parts.push(String(detail.code));
    if (detail.hint && !parts.includes(String(detail.hint))) parts.push(String(detail.hint));
    if (Array.isArray(detail.steps) && detail.steps.length) {
      parts.push(detail.steps.map(String).join(" → "));
    }
    if (detail.request_id) parts.push(`(ref: ${detail.request_id})`);
    if (parts.length) return parts.join(" — ");
    try {
      return JSON.stringify(detail);
    } catch {
      return "Request failed";
    }
  }
  return error?.message || "Request failed";
}

/**
 * Maps disbursement / financial API errors to user-facing messages.
 * Prefer structured bank-file validation (message + hint + issues).
 */
export function formatFinancialApiError(error, { context = "action" } = {}) {
  const status = error?.response?.status;
  const raw = error?.response?.data?.detail ?? error?.response?.data;
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    const parts = [];
    if (raw.message) parts.push(String(raw.message));
    if (raw.hint) parts.push(String(raw.hint));
    if (Array.isArray(raw.steps) && raw.steps.length) {
      parts.push(`Steps: ${raw.steps.map(String).join(" → ")}`);
    }
    if (Array.isArray(raw.issues) && raw.issues.length) {
      const sample = raw.issues.slice(0, 8).map((i) => {
        const who = i.employee_code || i.employee_name || i.employee_id || "Employee";
        const fix = i.how_to_fix ? ` — ${i.how_to_fix}` : "";
        return `• ${who}: ${i.title || i.detail}${fix}`;
      });
      parts.push(sample.join("\n"));
      if (raw.issues.length > 8) {
        parts.push(`…and ${raw.issues.length - 8} more`);
      }
    } else if (Array.isArray(raw.excluded_employees) && raw.excluded_employees.length) {
      parts.push(
        `Excluded:\n${raw.excluded_employees
          .slice(0, 10)
          .map((n) => `• ${n}`)
          .join("\n")}`
      );
    } else if (Array.isArray(raw.errors) && raw.errors.length) {
      parts.push(raw.errors.slice(0, 10).map(String).join("\n"));
    }
    if (parts.length) return parts.join("\n\n");
  }

  const detail = getApiErrorDetail(error);
  const lower = String(detail).toLowerCase();

  if (status === 409) {
    if (lower.includes("idempotency")) {
      return "This request was already processed. The list will refresh.";
    }
    if (lower.includes("not ready for finance")) {
      return "Finance approval is not available yet — complete HR approval first.";
    }
    if (lower.includes("not approved")) {
      return "Payout is only available after the batch is fully approved.";
    }
    if (lower.includes("already approved")) {
      return "This approval step is already complete. Refreshing the batch list.";
    }
    if (lower.includes("finance approval is required") || lower.includes("fully approved")) {
      return detail;
    }
    if (lower.includes("duplicate") || lower.includes("unique") || lower.includes("already exists")) {
      return "A salary batch already exists for this payroll run.";
    }
    return detail;
  }

  if (status === 400) {
    if (lower.includes("bank file") || lower.includes("validation")) {
      return detail;
    }
    if (lower.includes("no payable")) {
      return detail;
    }
    if (lower.includes("idempotency-key")) {
      return "System configuration error: missing idempotency key. Please retry.";
    }
  }

  if (status === 404) {
    return "Salary batch not found or you do not have access.";
  }

  if (status === 403) {
    return "You do not have permission to perform this action.";
  }

  if (status === 500 || (raw && typeof raw === "object" && raw.code === "INTERNAL_ERROR")) {
    return (
      (raw && typeof raw === "object" && raw.message) ||
      "Something went wrong on the server. Please try Generate File again to download the bank file."
    );
  }

  return detail || `Failed to complete ${context}`;
}

/** Structured bank-file validation payload for rich UI panels. */
export function parseBankFileValidationError(error) {
  const raw = error?.response?.data?.detail ?? error?.response?.data;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  // Generic 500 envelopes also have message/steps — do not treat as bank validation.
  if (raw.code === "INTERNAL_ERROR") return null;
  if (!raw.message && !Array.isArray(raw.issues)) return null;
  const looksLikeBankFile =
    Array.isArray(raw.issues) ||
    Array.isArray(raw.excluded_employees) ||
    Boolean(raw.bank_code) ||
    raw.code === "PAYOUT_PROVIDER_NOT_CONFIGURED" ||
    /bank file|bank detail|payable|ifsc|salary batch|generate file|razorpay/i.test(
      `${raw.message || ""} ${raw.hint || ""}`
    );
  if (!looksLikeBankFile) return null;
  return {
    message: raw.message || "Bank file validation failed",
    hint: raw.hint || "",
    steps: Array.isArray(raw.steps) ? raw.steps : [],
    issues: Array.isArray(raw.issues) ? raw.issues : [],
    errors: Array.isArray(raw.errors) ? raw.errors : [],
    bankCode: raw.bank_code || "",
    errorCount: raw.error_count || (raw.issues?.length ?? 0),
  };
}
